import { useMemo, useState } from "react";
import { State } from "redux/store";
import classNames from "classnames";
import { useLocation, useNavigate, useParams } from "react-router";
import useVqlParams from "hooks/useVqlParams";
import { connect, useSelector } from "react-redux";
import TooltipWrapper from "views/uikit/TooltipWrapper";
import { isVqlQuery } from "helpers/utility/vqlHelpers";
import {
  GRANULARITY_PARAMETER,
  REVIEW_PARAMETER,
  PAGE_PARAMETER,
} from "helpers/constants/miscellaneous";

import {
  getIsDuplicatesThresholdEnabled,
  getSimilarityThreshold,
  getSimilarityVertexCluster,
  getThresholdsRange,
} from "redux/SingleDataset/selectors";
import HighGranularityIcon from "assets/icons/GranularityIcons/HighGranularityIcon";
import LowGranularityIcon from "assets/icons/GranularityIcons/LowGranularityIcon";

import { ImInfo } from "react-icons/im";
import styles from "./style.module.scss";
import useAllQueryParamsFilters from "hooks/useAllQueryParamsFilters";

const TOOLTIP_FOR_DUPLICATE_CLUSTER =
  "Granularity level cannot be changed when the Duplicates view is active.";
const GRANULARITY_INFO_TOOLTIP = `Control the level of similarity between Images in a Cluster. A higher threshold groups more similar images together, resulting in smaller, more precise clusters.`;

const GranularityStepIcon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="18"
      viewBox="0 0 14 14"
      fill="none"
    >
      <circle cx="7" cy="7" r="6.5" fill="#151928" stroke="white" />
    </svg>
  );
};

const GranularityStepSelectedIcon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="18"
      viewBox="0 0 14 14"
      fill="none"
    >
      <circle cx="7" cy="7" r="6.5" fill="#151928" stroke="white" />

      <circle cx="7" cy="7" r="3.5" fill="white" stroke="white" />
    </svg>
  );
};

const GranularitySlider = ({
  threasholdsRange,
  isVertexSelected,
  isDuplicateThresholdEnabled,
}: any) => {
  const [hoveredTooltip, setHoveredTooltip] = useState<number | null>(null);
  const { vqlQueries } = useVqlParams();

  const { clusterId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const queryParams = useAllQueryParamsFilters();
  const similarityThreshold = useSelector((state: State) =>
    getSimilarityThreshold(state, queryParams[GRANULARITY_PARAMETER]),
  );
  const inReview = searchParams.has(REVIEW_PARAMETER);
  const duplicateThresholdApplied = !!vqlQueries.find((query) =>
    isVqlQuery(query, "duplicates"),
  );
  const isDuplicateCluster =
    isDuplicateThresholdEnabled && duplicateThresholdApplied;

  const handleGranularityChange = async (value: string) => {
    searchParams.set(GRANULARITY_PARAMETER, value);
    searchParams.set(PAGE_PARAMETER, "1");
    navigate({ search: `?${searchParams.toString()}` });
  };

  const handleTooltipMessage = (index: number): string => {
    const totalThresholds =
      threasholdsRange.maxThreshold - threasholdsRange.minThreshold;

    const areTotalThreasholdsOdd = (totalThresholds - 1) % 2 !== 0;
    const middleThreashold =
      areTotalThreasholdsOdd && Math.floor(totalThresholds / 2);

    switch (index) {
      case 0:
        return "Lower Threshold";
      case totalThresholds:
        return "Higher Threshold";
      case middleThreashold:
        return "Mid Threshold";
      default:
        return "";
    }
  };

  const displayThresholdTooltip = (index: number): JSX.Element | null => {
    const message = handleTooltipMessage(index);

    return message && hoveredTooltip === index ? (
      <div className={styles.threasholdTooltip} key={index}>
        {message}
      </div>
    ) : null;
  };

  if (similarityThreshold) {
    return (
      <div
        className={classNames(styles.granularityWrapper, {
          [styles.disabledWrapper]: isDuplicateCluster,
        })}
        style={
          !!clusterId || isVertexSelected || inReview
            ? { visibility: "hidden" }
            : undefined
        }
      >
        <span className={styles.granularityTitle}>Granularity</span>

        <div
          className={styles.granularityInfoIcon}
          data-tooltip-content={
            isDuplicateCluster
              ? TOOLTIP_FOR_DUPLICATE_CLUSTER
              : GRANULARITY_INFO_TOOLTIP
          }
          data-tooltip-id="data-page-granularity-box"
          data-tooltip-place="top"
        >
          <ImInfo size="13px" />
          <TooltipWrapper
            maxWidth={isDuplicateCluster ? "max-content" : "360px"}
            customClass={isDuplicateCluster ? "granularityInfo" : ""}
            id="data-page-granularity-box"
          />
        </div>
        <HighGranularityIcon />
        <div className={styles.inputContainer}>
          <div className={styles.ticks}>
            {[
              ...Array(
                threasholdsRange.maxThreshold -
                  threasholdsRange.minThreshold +
                  1,
              ),
            ].map((_: any, index: number) => {
              const stringIndex = "" + index;
              return similarityThreshold === stringIndex ? (
                <div
                  className={styles.selectedtick}
                  key={index}
                  onMouseEnter={() => setHoveredTooltip(index)}
                  onMouseLeave={() => setHoveredTooltip(null)}
                >
                  <GranularityStepSelectedIcon />
                  {displayThresholdTooltip(index)}
                </div>
              ) : (
                <div
                  className={styles.selectedtick}
                  key={index}
                  onClick={() => handleGranularityChange(stringIndex)}
                  onMouseEnter={() => setHoveredTooltip(index)}
                  onMouseLeave={() => setHoveredTooltip(null)}
                >
                  <GranularityStepIcon />
                  {displayThresholdTooltip(index)}
                </div>
              );
            })}
          </div>
        </div>
        <LowGranularityIcon />
      </div>
    );
  } else {
    return null;
  }
};

const mapStateToProps = (state: State) => {
  return {
    threasholdsRange: getThresholdsRange(state),
    isVertexSelected: !!getSimilarityVertexCluster(state),
    isDuplicateThresholdEnabled: getIsDuplicatesThresholdEnabled(state),
  };
};

export default connect(mapStateToProps)(GranularitySlider);
